import React, { FC, useState } from "react";
import { FileDownloadOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import { FormCsvDataDto } from "@green0meter/api-client";

import {
  StyledListItemIcon,
  StyledListItemText,
  StyledMenuItem,
} from "../../ActionsDropdown.styled";
import { getExportColumns } from "./CsvExport.const";

import { ConfirmationModal } from "@components/common";
import { useGetFormCsvData } from "@api";
import { useUserStore } from "@store/user";
import { useMultistepFormStore } from "@components/common/MultistepForm";

const CsvExport: FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUserStore();
  const {
    formSettings: {
      formBasics: { id, name },
    },
  } = useMultistepFormStore();
  const [modalOpen, setModalOpen] = useState(false);
  const { getFormCsvDataAsync, isPendingGetFormCsvData } = useGetFormCsvData(user);

  const handleOpenModal = (): void => {
    setModalOpen(true);
  };

  const handleCloseModal = (): void => {
    setModalOpen(false);
  };

  const handleExport = async (): Promise<void> => {
    const data = await getFormCsvDataAsync(id);
    const csv: string[] = [];
    const exportColumns = getExportColumns(i18n.resolvedLanguage ?? "cz");

    csv.push(exportColumns.map((col) => col.header).join(","));

    // Fields that may contain commas, quotes, or newlines and need quoting
    const textKeys = [
      "group",
      "groupEN",
      "question",
      "questionEN",
      "notes",
      "value",
    ];

    data.forEach((row: FormCsvDataDto) => {
      const exportRow: string[] = [];

      exportColumns.forEach(({ key }) => {
        const value = row[key as keyof FormCsvDataDto];

        if (value === null || value === undefined) {
          exportRow.push("");
        } else if (textKeys.includes(key)) {
          const strValue = String(value).replace(/"/g, '""');

          exportRow.push(`"${strValue}"`);
        } else {
          exportRow.push(String(value));
        }
      });
      csv.push(exportRow.join(","));
    });

    const now = new Date();

    saveAs(
      new Blob([csv.join("\n")], { type: "text/csv;charset=utf-8;" }),
      `${name}_export_${now.getTime()}.csv`,
    );

    setModalOpen(false);
  };

  return (
    <>
      <StyledMenuItem onClick={handleOpenModal}>
        <StyledListItemIcon>
          <FileDownloadOutlined fontSize="medium" />
        </StyledListItemIcon>
        <StyledListItemText>{t("forms.exportCSV")}</StyledListItemText>
      </StyledMenuItem>
      <ConfirmationModal
        open={modalOpen}
        onClose={handleCloseModal}
        onConfirm={handleExport}
        isLoading={isPendingGetFormCsvData}
        noCancel={isPendingGetFormCsvData}
        title={
          <>
            <FileDownloadOutlined fontSize="large" />
            <br />
            {t("forms.exportCSVTitle")}
          </>
        }
        description={t("forms.exportCSVDescription")}
        confirmText={
          isPendingGetFormCsvData
            ? t("forms.exportCsv.inProgress")
            : t("common.continue")
        }
      />
    </>
  );
};

export default CsvExport;
